import { useMutation } from '@apollo/react-hooks'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import { historyVariables } from '../lib/records'
import type { CostInvoice, CostInvoiceStatus, CostLineInput } from '../types'
import { WRITE, useCostWriteQueue, type Mutate, type Reload, type Vars } from './useCostWriteQueue'

/** Invoice and invoice-attachment writes (spec §6), with the same reload rules as useChangeMutations. */
export function useInvoiceMutations(projectId: string, onDetailError?: (invoiceId: string, error: unknown) => void) {
  const { saving, run } = useCostWriteQueue()
  const [create] = useMutation(Mutation.CREATE_COST_INVOICE, WRITE)
  const [update] = useMutation(Mutation.UPDATE_COST_INVOICE, WRITE)
  const [remove] = useMutation(Mutation.DELETE_COST_INVOICE, WRITE)
  const [setLines] = useMutation(Mutation.SET_COST_INVOICE_LINES, WRITE)
  const [setStatus] = useMutation(Mutation.SET_COST_INVOICE_STATUS, WRITE)
  const [addAttachment] = useMutation(Mutation.ADD_COST_ATTACHMENT, WRITE)
  const [removeAttachment] = useMutation(Mutation.REMOVE_COST_ATTACHMENT, WRITE)
  const detail = (id: string): Reload[] => [
    { query: Query.COST_INVOICE, variables: { id }, onError: onDetailError && ((e: unknown) => onDetailError(id, e)) },
    { query: Query.COST_ACTIVITY_LOG, variables: historyVariables(projectId, 'INVOICE', id) },
  ]
  const register: Reload[] = [{ query: Query.COST_INVOICES, variables: { projectId, filter: {} } }]
  const m = (fn: unknown) => fn as Mutate

  return {
    saving,
    create: (input: Vars) => run<{ createCostInvoice: CostInvoice }>(m(create), { projectId, input }, register),
    update: (id: string, input: Vars) => run(m(update), { id, input }, detail(id)),
    setLines: (id: string, lines: CostLineInput[]) => run(m(setLines), { id, lines }, detail(id)),
    /** `from` is the status the person saw: the server refuses the change when it no longer holds (spec §11). */
    setStatus: (id: string, status: CostInvoiceStatus, from: CostInvoiceStatus, paidAt?: string | null) =>
      run(m(setStatus), { id, status, from, paidAt: paidAt ?? null }, detail(id)),
    remove: (id: string) => run(m(remove), { id }, register, detail(id)),
    addAttachment: (id: string, source: { fileId: string } | { docRevisionId: string }) => run(m(addAttachment), { target: { invoiceId: id }, ...source }, detail(id)),
    removeAttachment: (id: string, attachmentId: string) => run(m(removeAttachment), { id: attachmentId }, detail(id)),
  }
}

export type InvoiceMutations = ReturnType<typeof useInvoiceMutations>
